import { getDirectionsUrl } from '@/components/azure-map/azureMapsSdk'
import { getRelationshipStyle } from '@/routes/map/SitesMapPage.layers'
import type { MapSite } from '@/routes/map/SitesMapPage.types'
import { Button } from 'devextreme-react/button'
import { List } from 'devextreme-react/list'

type SitesMapSitePanelProps = {
  sites: MapSite[]
  expanded: boolean
  radiusKm: number | null
  onToggleExpanded: () => void
  onSelectSite: (site: MapSite | null) => void
}

export const SiteDetail = ({ site, onClose }: { site: MapSite; onClose: () => void }) => {
  const relationshipStyle = getRelationshipStyle(site.relationshipType)

  return (
    <div className={'sites-map-panel__site'}>
      <div className={'sites-map-panel__site-header'}>
        <div>
          <h4>{site.companyName}</h4>
          <p>
            {site.siteName} - {site.city} ({site.provinceName})
          </p>
        </div>
        <Button
          icon={'close'}
          stylingMode={'text'}
          onClick={onClose}
          elementAttr={{ 'aria-label': 'Chiudi dettaglio sede' }}
        />
      </div>

      <div className={'sites-map-panel__tags'}>
        {site.relationshipType && (
          <span
            className={'sites-map-panel__badge'}
            style={{
              backgroundColor: relationshipStyle.background,
              color: relationshipStyle.color,
              borderColor: relationshipStyle.border,
            }}
          >
            {site.relationshipType}
          </span>
        )}
        {site.agentName && <span>Agente: {site.agentName}</span>}
        {site.distanceKm !== undefined && (
          <span className={'sites-map-panel__distance'}>Distanza: {site.distanceKm.toFixed(1)} km</span>
        )}
      </div>

      <div className={'sites-map-panel__actions'}>
        <Button
          text={'Apri'}
          icon={'home'}
          type={'default'}
          disabled={!site.companyId}
          onClick={() => window.open(`/clients/${site.companyId}/sites`, '_blank')}
        />
        <Button
          text={site.phone ? `Chiama (${site.phone})` : 'Chiama (N/D)'}
          icon={'tel'}
          type={'success'}
          disabled={!site.phone}
          onClick={() => {
            window.location.href = `tel:${site.phone}`
          }}
        />
        <Button
          text={'Naviga'}
          icon={'car'}
          onClick={() => {
            window.location.href = getDirectionsUrl({ lat: site.latitude, lon: site.longitude }, site.companyName)
          }}
        />
      </div>
    </div>
  )
}

const SiteListItem = ({ site }: { site: MapSite }) => (
  <div className={'sites-map-panel__list-item'}>
    <div className={'sites-map-panel__list-item-header'}>
      <strong>{site.companyName}</strong>
      {site.distanceKm !== undefined && (
        <span className={'sites-map-panel__distance'}>{site.distanceKm.toFixed(1)} km</span>
      )}
    </div>
    <span>
      {site.city} ({site.provinceName}) - {site.relationshipType || 'N/D'} {site.isMainSite && '★'}
    </span>
  </div>
)

const SitesMapSitePanel = ({ sites, expanded, radiusKm, onToggleExpanded, onSelectSite }: SitesMapSitePanelProps) => {
  const panelClassName = ['sites-map-panel', expanded ? 'sites-map-panel--expanded' : ''].filter(Boolean).join(' ')

  return (
    <div className={panelClassName}>
      <div
        role={'button'}
        tabIndex={0}
        className={'sites-map-panel__handle'}
        onClick={onToggleExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onToggleExpanded()
        }}
      >
        <div className={'sites-map-panel__title'}>
          {`Elenco Sedi (${sites.length})`}
          {radiusKm !== null && <span className={'sites-map-panel__radius'}>Raggio: {radiusKm} km</span>}
        </div>
        <span>{expanded ? '▼ Riduci' : '▲ Espandi'}</span>
      </div>

      <div className={'sites-map-panel__content'}>
        {sites.length > 0 && (
          <List
            dataSource={sites}
            height={expanded ? 'calc(50vh - 60px)' : '100px'}
            itemRender={(site: MapSite) => <SiteListItem site={site} />}
            onItemClick={(e) => onSelectSite(e.itemData ?? null)}
          />
        )}
        {sites.length === 0 && <p className={'sites-map-panel__empty'}>Nessuna sede trovata con i filtri correnti.</p>}
      </div>
    </div>
  )
}

export default SitesMapSitePanel
